import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, Grid, IconButton, InputAdornment, Stack,
  TextField as MuiTextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import retailPipelineApi from '../../services/retailPipelineApi';
import StatusBadge, { PIPELINE_STATUS_MAP } from '../../components/shared/StatusBadge';
import PageHeader from '../../components/shared/PageHeader';
import StatCard from '../../components/shared/StatCard';
import DataTable from '../../components/shared/DataTable';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';
import { getAxiosErrorMessage } from '../../utils/getAxiosErrorMessage';

const RETAIL_STAGE_MAP = {
  ...PIPELINE_STATUS_MAP,
  Origination: { variant: 'info', label: 'Origination' },
  Processing: { variant: 'cyan', label: 'Processing' },
  Underwriting: { variant: 'info', label: 'Underwriting' },
  Closed: { variant: 'success', label: 'Closed' },
  Funded: { variant: 'cyan', label: 'Funded' },
};

const fmtDollar = (v) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const RetailPipelinePage = () => {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = retailPipelineApi.endpoints.getRetailPipeline.useQuery();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('lastUpdated');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = useCallback((column) => {
    setSortDirection(prev => sortColumn === column ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortColumn(column);
  }, [sortColumn]);

  const pipelineLoans = useMemo(() => {
    if (!data?.loans) return [];
    let loans = [...data.loans];

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      loans = loans.filter(
        (l) =>
          (l.borrowerNames || '').toLowerCase().includes(q) ||
          (l.primaryBorrowerFullName || '').toLowerCase().includes(q) ||
          (l.loanNumber?.toString() || '').includes(q) ||
          (l.loanOriginator || '').toLowerCase().includes(q) ||
          (l.propertyState || '').toLowerCase().includes(q) ||
          (l.programName || '').toLowerCase().includes(q)
      );
    }

    loans.sort((a, b) => {
      let av = a[sortColumn], bv = b[sortColumn];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return loans;
  }, [data, searchTerm, sortColumn, sortDirection]);

  const columns = useMemo(() => [
    {
      key: 'loanNumber',
      label: 'Loan #',
      renderCell: (loan) => (
        <Typography variant="body2" fontWeight={600} color="primary">{loan.loanNumber || '—'}</Typography>
      ),
    },
    {
      key: 'borrowerNames',
      label: 'Borrower',
      renderCell: (loan) => (
        <>
          <Typography variant="body2">{loan.borrowerNames || loan.primaryBorrowerFullName || '—'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {[loan.propertyCity, loan.propertyState].filter(Boolean).join(', ') || ''}
          </Typography>
        </>
      ),
    },
    {
      key: 'loanOriginator',
      label: 'Loan Officer',
      renderCell: (loan) => (
        <>
          <Typography variant="body2">{loan.loanOriginator || '—'}</Typography>
          <Typography variant="caption" color="text.secondary">{loan.processor || ''}</Typography>
        </>
      ),
    },
    {
      key: 'loanAmount',
      label: 'Amount',
      align: 'right',
      renderCell: (loan) => (
        <Typography variant="body2" fontWeight={600} className="tabular-nums">{fmtDollar(loan.loanAmount)}</Typography>
      ),
    },
    {
      key: 'stage',
      label: 'Stage',
      renderCell: (loan) => <StatusBadge status={loan.stage} map={RETAIL_STAGE_MAP} />,
    },
    {
      key: 'programName',
      label: 'Program',
      renderCell: (loan) => <Typography variant="caption">{loan.programName || '—'}</Typography>,
    },
    {
      key: 'noteRate',
      label: 'Rate',
      align: 'right',
      renderCell: (loan) => (
        <Typography variant="body2" className="tabular-nums">{loan.noteRate ? `${loan.noteRate.toFixed(3)}%` : '—'}</Typography>
      ),
    },
    {
      key: 'mortgageType',
      label: 'Type',
      renderCell: (loan) => <Typography variant="body2">{loan.mortgageType || '—'}</Typography>,
    },
    {
      key: 'lockExpirationDate',
      label: 'Lock Exp',
      renderCell: (loan) => {
        const lockExpiring = loan.lockExpirationDate && new Date(loan.lockExpirationDate) < new Date(Date.now() + 3 * 86400000);
        const lockExpired = loan.lockExpirationDate && new Date(loan.lockExpirationDate) < new Date();
        return (
          <Typography
            variant="body2"
            fontWeight={lockExpired || lockExpiring ? 600 : 400}
            color={lockExpired ? 'error.main' : lockExpiring ? 'warning.main' : 'text.primary'}
          >
            {fmtDate(loan.lockExpirationDate)}
            {lockExpired && <WarningIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'text-bottom' }} />}
          </Typography>
        );
      },
    },
    {
      key: 'lastUpdated',
      label: 'Updated',
      renderCell: (loan) => (
        <Typography variant="caption" color="text.secondary">{fmtDate(loan.lastUpdated)}</Typography>
      ),
    },
  ], []);

  if (isLoading) {
    return <LoadingSkeleton variant="page" cards={4} />;
  }

  if (error) {
    return (
      <Alert severity="error" action={<Button color="error" size="small" onClick={refetch}>Retry</Button>}>
        Failed to load retail pipeline data. {getAxiosErrorMessage(error, '')}
      </Alert>
    );
  }

  const s = data?.summary;

  return (
    <>
      <PageHeader
        title="Retail Pipeline"
        subtitle="Retail loan pipeline overview"
        actions={(
          <Tooltip title="Refresh">
            <IconButton onClick={refetch}><RefreshIcon /></IconButton>
          </Tooltip>
        )}
      />

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, lg: 3 }}>
          <StatCard label="Total Loans" value={s?.totalActiveLoans || 0} icon={<ListAltIcon />} />
        </Grid>
        <Grid size={{ xs: 6, lg: 3 }}>
          <StatCard label="Pipeline Volume" value={fmtDollar(s?.pipelineVolume)} icon={<AccountBalanceOutlinedIcon />} />
        </Grid>
        <Grid size={{ xs: 6, lg: 3 }}>
          <StatCard label="Avg Loan Amount" value={fmtDollar(s?.avgLoanAmount)} icon={<PaidOutlinedIcon />} />
        </Grid>
        <Grid size={{ xs: 6, lg: 3 }}>
          <StatCard label="Avg FICO" value={s?.avgFico || '—'} icon={<CreditScoreIcon />} />
        </Grid>
      </Grid>

      {/* Pipeline table */}
      <Card>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5 }}>
          <MuiTextField
            size="small"
            placeholder="Search borrower, loan #, LO, state…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 320 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto !important' }}>
            {pipelineLoans.length} of {data?.loans?.length || 0} loans
          </Typography>
        </Stack>
        <DataTable
          columns={columns}
          rows={pipelineLoans}
          rowKey={(loan) => loan.loanId}
          onRowClick={(loan) => navigate(`/lender/retail-pipeline/${loan.loanId}`)}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          emptyProps={{
            title: searchTerm ? 'No loans match your search' : 'No loans in pipeline',
            description: searchTerm ? 'Try adjusting your search terms.' : undefined,
          }}
        />
      </Card>
    </>
  );
};

export default RetailPipelinePage;
